import React, { useState, useRef, useEffect, useCallback } from 'react';

import { AgGridReact } from 'ag-grid-react'; // AG Grid Component
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import './LeadConversionTiming.css'
import "ag-grid-enterprise";
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';

import { currencyFormatter, dateFormatter } from '../valueformatter';

export default function LeadConversionTiming(props) {
  const gridOptions= {
    sideBar: true,
    rowGroupPanelShow: 'always',
    // groupDefaultExpanded: 1,
    columnHoverHighlight: true,
    // groupIncludeFooter: true,
    groupIncludeTotalFooter: true,
    suppressAggFuncInHeader: true,
    autoGroupColumnDef: {
      cellRendererParams: {
        footerValueGetter: params => {
          const isRootLevel = params.node.level === -1;
          if (isRootLevel) {
            return 'Grand Total';
          }
          return `Sub Total (${params.value})`;
        },
      },
      headerName: 'Source',
    },
    getRowStyle: params => {
      // Grand Total row formatting
      if (params.node.group && params.node.level === -1) {
          return { backgroundColor: '#3F91B8', color: 'white', fontWeight: 'bold' };
      }
      // Subtotal row formatting
      if (params.node.group) {
          return { backgroundColor: '#b5c4ca', fontWeight: 'bold' };
      }
      // Alternating row colors
      return { backgroundColor: params.node.rowIndex % 2 === 0 ? '#f9f9f9' : '#c3e6f7' };
  },
    autoSizeStrategy: {
      type: 'fitCellContents'
    },
  };

  const gridRef = useRef(); // Optional - for accessing Grid's API

  // DefaultColDef sets props common to all Columns
  const defaultColDef = {
    filter: true,
  };

  // Group Row Agg func
    const getGroupRowAgg = useCallback((params) => {
        let result = {
            deals: 0,
            sold_same_day: 0,
            sold_after: 0,
        };

        params.nodes.forEach((node) => {
            const data = node.group ? node.aggData : node.data
            // Increment Total Calls
            result.deals += data.deals;
            // Increment Queued
            result.sold_same_day += data.sold_same_day;
            // Increment Answered
            result.sold_after += data.sold_after;
        });

        return result;
    });



  // Column Definitions: Defines the columns to be displayed.
  const colDefs = [
    { field: "source", headerName: "Source", enableRowGroup: true, rowGroup: true, sort: "asc", hide: true},
    { field: "settlement_officer", headerName: "Settlement Officer", enableRowGroup: true, sort: "asc",},
    { field: "deals", headerName: "Deals", cellStyle: { textAlign: 'right' },},
    { field: "sold_same_day", headerName: "Sold Same Day", cellStyle: { textAlign: 'right' },},
    { field: "sold_after", headerName: "Sold After", cellStyle: { textAlign: 'right' },},
  ];

  // Row Data: The data to be displayed.
  const [rowData, setRowData] = useState([]);

  // Preserving Filter Model
  const [filterModel, setFilterModel] = useState(null);  // Save current filters

  // Loading state to track when data is being fetched
  const [loading, setLoading] = useState(true);

  // Fetch data based on date range
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true before fetching
      if (gridRef.current) {
        // Save current filter state
        const currentFilterModel = gridRef.current.api.getFilterModel();
        setFilterModel(currentFilterModel);
      }

      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          replaceKeys: {
            "{{startDate}}": props.startDate,
            "{{endDate}}": props.endDate
          }
        })
      };
      const reqUrl = `${process.env.REACT_APP_QF_API}/${process.env.REACT_APP_OTG_ID}/data/e0OnACXUkj2eCfycoPTe`;

      try {
        const result = await fetch(reqUrl, requestOptions);
        const respData = await result.json();
        // Check for error 
        if(respData.status !== "success") {
          alert("Error fetching data: " + respData.msg);
          setRowData([]);
          return;
        }
        setRowData(respData.data);
      } catch (error) {
        console.error("ERROR: " + error);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchData();
  }, [props.reportName, props.startDate, props.endDate]);

  // After new data is set, reapply the filter model
  useEffect(() => {
    if (gridRef.current && filterModel) {
      gridRef.current.api.setFilterModel(filterModel);
    }
  }, [rowData, filterModel]);

  // Autosize the grouped column when the grid is first rendered
  const onFirstDataRendered = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
    }, 100);
  };

  // Autosizes all columns when row group is expanded
  const onRowGroupOpened = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
    }, 100);
  };

  // Autosizes all columns when row group is expanded
  const onColumnRowGroupChanged = (params) => {
    setTimeout(() => {
      params.columnApi.autoSizeAllColumns();
      // Get the row group columns
      const rowGroupCols = params.columnApi.getRowGroupColumns();
      if (rowGroupCols.length > 0) {
        // Get the last added group column
        const lastGroupCol = rowGroupCols[rowGroupCols.length - 1];
        // Apply sort to the grouped column
        params.columnApi.applyColumnState({
          state: [{ colId: lastGroupCol.getColId(), sort: 'asc' }]
        });
      }
    }, 100);
  };

  return (
    // wrapping container with theme & size
    <div
      className="ag-theme-quartz h-78 greyBg" // applying the grid theme
    >
      <div id="summaryBox">
        <p id="summaryText">
          <strong>Lead Conversion Timing</strong><br />
        </p>
      </div>
      <div style={{ position: 'relative', height: '100%' }}> {/* New wrapper for AG Grid */}
        <AgGridReact
          onFirstDataRendered={onFirstDataRendered}
          onRowGroupOpened={onRowGroupOpened}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          enableCharts={true}
          enableRangeSelection={true}
          rowData={rowData}
          columnDefs={colDefs}
          getGroupRowAgg={getGroupRowAgg}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          modules={[ServerSideRowModelModule, RowGroupingModule]}
          defaultSideBar={false}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
              },
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              }
            ]
          }}
        />
        {loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>
    </div>
  )
}